import { ChevronDownIcon, ClockIcon, UserMinusIcon, UserPlusIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import type { Task } from "@/lib/types";
import { cn, entrySeconds, formatDateTime, formatDuration, totalSeconds } from "@/lib/utils";
import { Avatar, Button, Tag, UserChip } from "../ui";
import { useNow, useTaskActions } from "./hooks";
import { ChoicePopover } from "./pickers";

export function RunningDot({ className }: { className?: string }) {
  return <span className={cn("inline-block size-2 shrink-0 rounded-full bg-[var(--dot-green)] animate-pulse-dot", className)} title="Timer running" />;
}

/** Compact assignee avatars + names for cards and table cells. */
export function AssigneeList({ task, max = 3, names = true }: { task: Pick<Task, "assignees">; max?: number; names?: boolean }) {
  const { userName } = useProject();
  if (!task.assignees.length) return null;
  const shown = task.assignees.slice(0, max);
  const rest = task.assignees.length - shown.length;
  if (names && task.assignees.length === 1) {
    const a = task.assignees[0];
    return <UserChip id={a.userId} name={userName(a.userId)} size={18} className="text-[13px]" />;
  }
  return (
    <span className="flex items-center">
      {shown.map((a) => (
        <Avatar key={a.userId} id={a.userId} name={userName(a.userId)} size={20} className="-me-1 ring-2 ring-[var(--surface)]" />
      ))}
      {rest > 0 && <span className="ms-2 text-xs text-muted">+{rest}</span>}
    </span>
  );
}

export function AssigneesRowValue({ task }: { task: Task }) {
  const { pid, me, canEdit, isOwner, assignable, userName } = useProject();
  const { run } = useTaskActions();
  const assigned = task.assignees.some((a) => a.userId === me.id);
  const unassignedMembers = assignable.filter((m) => !task.assignees.some((a) => a.userId === m.id));

  return (
    <div className="flex min-h-[34px] flex-wrap items-center gap-2 px-2 py-1 text-sm">
      {task.assignees.length === 0 && <span className="text-faint">Empty</span>}
      {task.assignees.map((a) => (
        <UserChip key={a.userId} id={a.userId} name={userName(a.userId)} size={20} />
      ))}
      {canEdit && (
        <span className="flex items-center gap-1">
          {assigned ? (
            <Button size="sm" variant="ghost" icon={UserMinusIcon} onClick={() => run(api.unassign(pid, task.id, me.id))}>
              Unassign me
            </Button>
          ) : (
            <Button size="sm" variant="ghost" icon={UserPlusIcon} onClick={() => run(api.assign(pid, task.id, me.id))}>
              Assign me
            </Button>
          )}
          {isOwner && unassignedMembers.length > 0 && (
            <ChoicePopover
              value={null}
              badge="plain"
              title="Assign a team member"
              choices={unassignedMembers.map((m) => ({ id: m.id, label: m.name }))}
              onChange={(id) => id && run(api.assign(pid, task.id, id))}
              trigger={(t) => (
                <Button ref={t.ref} onClick={t.onClick} size="sm" variant="ghost">
                  Assign others
                </Button>
              )}
            />
          )}
        </span>
      )}
    </div>
  );
}

/** Per-person assignment status and time tracked (live), plus the session log. */
export function TimeTracking({ task }: { task: Task }) {
  const { pid, me, isOwner, userName, bundle, assigneeStatusById } = useProject();
  const { run } = useTaskActions();
  const running = task.time.some((t) => t.openSince);
  const now = useNow(1000, running);
  const [showLog, setShowLog] = useState(false);

  if (task.time.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-line px-3 py-3 text-sm text-faint">
        Nobody has worked on this task yet. Time is tracked automatically while someone is assigned.
      </div>
    );
  }

  const people = [...task.time].sort((a, b) => Number(!!b.openSince) - Number(!!a.openSince) || entrySeconds(b, now) - entrySeconds(a, now));

  return (
    <div className="overflow-hidden rounded-lg border border-line">
      <div className="flex items-center justify-between border-b border-line bg-hover/40 px-3 py-2 text-sm">
        <span className="flex items-center gap-1.5 font-medium">
          <ClockIcon className="size-4 text-muted" /> Time on task
        </span>
        <span className="flex items-center gap-2 font-medium tabular-nums">
          {running && <RunningDot />}
          {formatDuration(totalSeconds(task.time, now), { seconds: running })}
        </span>
      </div>
      <ul className="divide-y divide-line">
        {people.map((entry) => {
          const assignee = task.assignees.find((a) => a.userId === entry.userId);
          const status = assignee?.statusId ? assigneeStatusById.get(assignee.statusId) : undefined;
          const canSetStatus = assignee && (entry.userId === me.id || isOwner);
          return (
            <li key={entry.userId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
              <UserChip id={entry.userId} name={userName(entry.userId)} className={cn("min-w-0 flex-1", !assignee && "opacity-60")} />
              {assignee ? (
                <ChoicePopover
                  value={assignee.statusId}
                  disabled={!canSetStatus}
                  title={entry.userId === me.id ? "Your status on this task" : `${userName(entry.userId)}'s status`}
                  choices={[{ id: null, label: "No status" }, ...bundle.assigneeStatuses.map((s) => ({ id: s.id, label: s.name, color: s.color }))]}
                  onChange={(id) => run(api.setAssigneeStatus(pid, task.id, entry.userId, id))}
                  trigger={(t) => (
                    <button
                      ref={t.ref}
                      onClick={t.onClick}
                      type="button"
                      disabled={!canSetStatus}
                      className={cn("flex items-center gap-1 rounded-md px-1.5 py-0.5", canSetStatus && "hover:bg-hover")}
                    >
                      {status ? <Tag color={status.color}>{status.name}</Tag> : <span className="text-faint">{canSetStatus ? "Set status" : "No status"}</span>}
                      {canSetStatus && <ChevronDownIcon className="size-3.5 text-faint" />}
                    </button>
                  )}
                />
              ) : (
                <span className="text-xs text-faint">no longer assigned</span>
              )}
              <span className="flex w-28 items-center justify-end gap-1.5 text-end tabular-nums">
                {entry.openSince && <RunningDot />}
                {formatDuration(entrySeconds(entry, now), { seconds: !!entry.openSince })}
              </span>
            </li>
          );
        })}
      </ul>
      <button type="button" onClick={() => setShowLog(!showLog)} className="w-full border-t border-line px-3 py-1.5 text-start text-xs text-muted hover:bg-hover">
        {showLog ? "Hide" : "Show"} assignment log ({task.sessions.length})
      </button>
      {showLog && (
        <ul className="max-h-60 overflow-y-auto border-t border-line px-3 py-2 text-xs scroll-thin">
          {task.sessions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-2 py-1">
              <span className="w-32 truncate font-medium" dir="auto">
                {userName(s.userId)}
              </span>
              <span className="text-muted">
                {formatDateTime(s.startedAt)} → {s.endedAt ? formatDateTime(s.endedAt) : "now"}
              </span>
              <span className="ms-auto tabular-nums">
                {formatDuration(((s.endedAt ? new Date(s.endedAt).getTime() : now) - new Date(s.startedAt).getTime()) / 1000)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

